<script setup lang="ts">
import { isEmpty } from 'es-toolkit/compat'
import { gsap } from 'gsap'
import { CustomEase } from 'gsap/CustomEase'
import { useConfetti, useEventListener } from '#imports'

definePageMeta({
    layout: false,
    alias: ['/promo-wheel', '/ht-promo-wheel']
})

// Add a page-scoped class to <body> so the CSS var (--body-bg) only applies on this page
useHead({
    bodyAttrs: { class: 'has-bonus-wheel-bg' },
})

const route = useRoute()

// We only want page content on the new ht-promo-wheel page, used for the splittest
const wheelContentSlug = route.path === '/ht-promo-wheel' ? 'ht-promo-wheel' : undefined
const { data: pageData } = await usePageData(wheelContentSlug)

const pageEventName = String(route.path || 'bonus_wheel')?.replace('/', '')?.replace('-', '_')

const { trackEvent } = useTracking()
const dayjs = useDayjs()

const { getHead, getSeoMeta } = useContentSeo(
    pageData.value?.seo || {
        metaTitle: 'Happy Tiger Online Slots and Casino',
        metaDescription: 'Happy Tiger™ - Win big time bonuses on our Bonus Wheel of Fortune',
        metaRobots: 'noindex',
    }
)

useHead(getHead())

useSeoMeta(getSeoMeta())

useRealVh()

const wheelStep = ref(0)

// Headline texts set and used in relation to wheelStep
// Duplications of text is used to keep only one central state and make core less complex
const headline = [
    'Spin & Win <small style="font-size:55%;">Win Your bonus</small>',
    'Win Your <small style="font-size:85%;">Bonus</small>',
    'Win Your <small style="font-size:85%;">Bonus</small>',
    'Win Your <small style="font-size:85%;">Bonus</small>',
    'You win <small style="font-size:80%;">The Best Bonus</small>',
]
const activeTitle = computed(() => headline[wheelStep.value])

// Cta texts set and used in relation to wheelStep
const cta = [
    'Spin now',
    'Spinning',
    'Stop',
    'Stopping',
    'Continue'
]
const activeCta = computed(() => cta[wheelStep.value])

const disableButton = ref(false)

// Animation Controls
const infiniteRotationTime = 50
const totalRotationTime = 15
const windUpTime = 1
const confettiTime = 2
const stopBtnSuspense = 2
const confettiColors = ['#FFD700', '#FF6B35', '#F7931E']

// Computed property for CSS duration
const windupTimeCss = computed(() => `${windUpTime}s`)

// Custom eases are made with GSAP's [Ease Visualizer](https://greensock.com/ease-visualizer)
// Modify an existing custom ease by copying/pasting the SVG path into the Ease Visualizer
gsap.registerPlugin(CustomEase)
CustomEase.create('spin', 'M0,0 C0.059,0.393 0.287,0.62 0.5,0.8 0.591,0.877 0.78,1 1,1')

const rotationTween = ref<gsap.core.Tween | gsap.core.Timeline | null>(null)
const wheelElement = ref<HTMLElement | null>(null)

// Trigger intro animation after 2 seconds
async function handleButtonPress() {
    if (wheelStep.value < 4) {
        return spinTheWheel(wheelStep.value)
    }

    await navigateTo({
        query: {
            auth: 'signup',
        }
    })
}

function initiateFinalWheelReward() {
    wheelStep.value = 4
    disableButton.value = false

    // Trigger confetti with canvas-confetti
    const end = dayjs().add(confettiTime, 'second')
    function frame() {
        useConfetti({
            particleCount: 4,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.8 },
            colors: confettiColors,
        })
        useConfetti({
            particleCount: 4,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.8 },
            colors: confettiColors,
        })

        if (dayjs().isBefore(end)) {
            requestAnimationFrame(frame)
        }
    }
    requestAnimationFrame(frame)
}

function spinTheWheel(step: number) {
    if (wheelElement.value) {
        // Get current rotation from GSAP
        const currentRotation = gsap.getProperty(wheelElement.value, 'rotation') as number

        // Calculate how much to spin to land on 0deg (or next 360deg interval)
        // Add the minSpinRotation value for the spinning effect
        const minSpinRotation = step === 0 ? 1920 : 720
        const targetRotation = Math.ceil((currentRotation + minSpinRotation) / 360) * 360

        // Stop the infinite rotation tween
        if (!rotationTween.value) {
            return
        }

        disableButton.value = true

        // 'Spin now' state
        if (step === 0) {
            wheelStep.value = 1

            rotationTween.value.kill()
            rotationTween.value = gsap.timeline()
                // Spin Windup animation
                .to(wheelElement.value, {
                    duration: windUpTime,
                    rotation: '-=30',
                    ease: 'sine.inOut',
                    force3D: true,
                })
                // Scale up wheelContainer
                // Reinitiate rotationTween with a max duration
                .to(wheelElement.value, {
                    duration: totalRotationTime,
                    rotation: targetRotation,
                    ease: 'spin',
                    force3D: true,
                    onComplete: initiateFinalWheelReward
                })

            setTimeout(() => {
                wheelStep.value = 2
                disableButton.value = false
            }, stopBtnSuspense * 1000)

            void trackEvent({
                event_name: `${pageEventName}_spin_now_clicked`,
                event_type: 'Click',
                meta_data: [],
            })
        }

        // 'Stop' state
        if (step === 2) {
            const rotationProgress = rotationTween.value.totalProgress()
            wheelStep.value = 3

            // If the wheel tween has less then half way to finish it's rotation
            // Let it finish and don't interrupt with new tween
            if (rotationProgress > 0.5) {
                return
            }

            rotationTween.value.kill()
            rotationTween.value = gsap.timeline()
                .to(wheelElement.value, {
                    duration: (totalRotationTime / 2),
                    rotation: targetRotation,
                    ease: 'sine.out',
                    force3D: true,
                    onComplete: initiateFinalWheelReward
                })
        }
    }
}

function initializeRotationTween() {
    if (wheelElement.value && wheelStep.value === 0) {
        rotationTween.value?.kill()
        gsap.set(wheelElement.value, { clearProps: 'all' })
        rotationTween.value = gsap.to(wheelElement.value, {
            duration: infiniteRotationTime,
            rotation: 360,
            ease: 'linear',
            repeat: -1,
            force3D: true,
        })
    }
}

void trackEvent({
    event_name: `landing_page_${pageEventName}_v_1_0_1`,
    event_type: 'Experiment',
    meta_data: [],
})

onMounted(() => {
    initializeRotationTween()
    useEventListener(window, 'resize', initializeRotationTween)
})

onUnmounted(() => {
    rotationTween.value?.kill()
})
</script>

<template>
    <div class="landing-page">
        <UIApp>
            <LandingPageNavBar />

            <main
                class="wheel"
                :class="[
                    `wheel--step-${wheelStep}`,
                    { 'wheel--spin-mode': wheelStep > 0 }
                ]"
            >
                <div class="wheel__title">
                    <!-- eslint-disable-next-line vue/no-v-html The content of activeTitle is safe -->
                    <h1 v-html="activeTitle" />
                </div>
                <div class="wheel__space">
                    <div class="wheel__container">
                        <div class="fit-parent wheel__bg">
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                            <svg viewBox="0 0 181 373"><path d="M89.5706 372.835L0.0907898 -129.643H180.191L89.5706 372.835Z" /></svg>
                        </div>
                        <div ref="wheelElement" class="wheel__image fit-parent wheel__image--wheel">
                            <NuxtImg
                                class="fit-parent"
                                src="/media/bonus/wheel.png"
                                alt="Wheel of Fortune"
                                provider="cloudflare"
                                preset="local"
                                loading="eager"
                                decoding="async"
                                fetchpriority="high"
                            />
                        </div>
                        <svg
                            class="wheel__image fit-parent wheel__image--mask"
                            width="1138"
                            height="1138"
                            viewBox="0 0 1138 1138"
                        >
                            <path
                                opacity="0.25"
                                d="M701.17 15.4307C951.682 75.0208 1138 300.252 1138 569C1138 883.25 883.25 1138 569 1138C254.75 1138 0 883.25 0 569C8.04396e-06 300.268 186.296 75.0478 436.785 15.4414L556.5 606H580L701.17 15.4307Z"
                                fill="#333333"
                            />
                            <path opacity="0.5" d="M818 569.5C818 706.743 706.743 818 569.5 818C432.257 818 321 706.743 321 569.5C321 432.257 432.257 321 569.5 321C706.743 321 818 432.257 818 569.5Z" fill="white" />
                            <path opacity="0.5" d="M801 569.5C801 697.354 697.354 801 569.5 801C441.646 801 338 697.354 338 569.5C338 441.646 441.646 338 569.5 338C697.354 338 801 441.646 801 569.5Z" fill="white" />
                            <path d="M646 569.5C646 611.75 611.75 646 569.5 646C527.25 646 493 611.75 493 569.5C493 527.25 527.25 493 569.5 493C611.75 493 646 527.25 646 569.5Z" fill="white" />
                        </svg>
                        <NuxtImg
                            class="wheel__marker"
                            src="/media/bonus/wheel-field-marker.png"
                            alt="Wheel Marker"
                            provider="cloudflare"
                            preset="local"
                            loading="lazy"
                        />
                    </div>
                </div>
                <UIButton
                    class="wheel__button"
                    :disabled="disableButton"
                    as="cta"
                    :color="wheelStep === 2 ? '#FF3C3C' : '#ff5f00'"
                    :shiny="false"
                    @click.prevent="handleButtonPress"
                >
                    {{ activeCta }} <UIIcon v-if="wheelStep < 3" name="refresh" size="24px" />
                </UIButton>
            </main>
            <StickyTerms
                title="Significant Conditions"
                is-landing-page
                reset-margin
                :sticky-config="{ container: '.landing-page', minHeight: 48, offset: 68 }"
            />
            <div v-if="!isEmpty(pageData?.dynamicContent)" class="trustpilot-wrap">
                <TrustpilotWidget type="horizontal" />
            </div>
            <LazyVisibleNimbusContentDynamicRenderer
                v-if="!isEmpty(pageData?.dynamicContent)"
                :content="pageData?.dynamicContent"
                :max-width="1024"
                color="#fff"
                hydrate-on-visible
            />
            <div class="landing-page__footer">
                <LazyVisibleFooterContainer :enable-terms="false" is-landing-page hydrate-on-visible />
            </div>
        </UIApp>
    </div>
</template>

<style>
body.has-bonus-wheel-bg {
    --body-bg: #b7e0eb;
}
</style>

<style scoped lang="scss">
$body-bg-blue: #1181c9;
$body-bg-light: #b7e0eb;
$body-bg-dark: #0a5f8a;
$yellow: #ff0;
$content-width: 1024px;

$navHeight: 60px;
$terms-offset: 68;
$spinModeEase: $easeInOutQuad;
$zoomEase: $easeOutQuad;
$rewardEase: $easeInOutBack;

.landing-page {
    --theme-background: var(--background, #{$body-bg});
    --login-button-color: #fff;
    --sticky-terms-background: #b7e0eb;
    --sticky-terms-color: #333;
    --windup-time: v-bind(windupTimeCss);

    position: relative;
    color: #fff;
    background-color: $body-bg-light;
    overflow: clip; // Hack to keep sticky terms sticky and avoid overflow-x scroll

    .theme-provider {
        --background: $body-bg-light;
        color: #fff;
    }

    // Temp hack of terms - making transparent gradient smoother
    &:deep(.offer-terms--sticky) {
        padding-top: 25px;
        background: none;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            width: 100vw;
            height: 100%;
            transform: translateX(-50%);
            z-index: -1;
            background: linear-gradient(
                to bottom,
                transparent 0,
                var(--sticky-terms-background) 25px,
                var(--sticky-terms-background) 100%
            );
        }
    }

    :deep(.dynamic-content) {
        background: $body-bg-blue;

        // The following dynamic content style mombo-jumpo
        // is to make the .media-text__media go full screen width on mobile
        .dynamic-content--contained > div:not(.media-text),
        .media-text__content {
            padding-left: 15px;
            padding-right: 15px;
        }

        .media-text {
            @include tablet {
                padding-left: 15px;
                padding-right: 15px;
            }

            .btn {
                @media (max-width: 430px) {
                    height: 60px;
                    width: 100%;
                }
            }

            .media-text__media,
            .media-text__image {
                @media (max-width: 430px) {
                    border-radius: 0;
                }
            }
        }

        .media-text__content {
            @include tablet {
                padding-left: 0;
                padding-right: 0;
            }
        }
    }

    .trustpilot-wrap {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        background-color: #fff;
    }

    &__footer {
        padding-top: 50px;
        background-color: var(--theme-background, $body-bg);
    }
}

.wheel {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 16px;
    // CSS custom prop --vh used to get real device vh. Requires useRealVh()
    height: calc((var(--vh, 0.9vh) * 100));
    min-height: 400px;
    max-height: 850px;
    padding-top: $navHeight;
    background-color: $body-bg-blue;
    overflow: hidden;
    transition: height 0.3s $easeOutQuad;

    @include mobile {
        min-height: 500px;
    }

    @include tablet {
        min-height: 650px;
        max-height: 750px;
    }

    &__space {
        flex: 3; // 75% of available height
        position: relative;
        transition: flex var(--windup-time) $spinModeEase;

        @media (min-height: 667px) {
            flex: 2; // 50% of available height
        }
    }

    &__title {
        flex: 1; // 25% of available height
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        transition: flex var(--windup-time) $spinModeEase;

        h1 {
            font-size: 40px;
            font-size: clamp(40px, 10vw, 56px);
            font-weight: 800;
            font-family: $heading-font-family;
            text-transform: uppercase;
            margin: 0;
            width: 104%; //Set to avoid text cut when angled on mobile
            transform: rotate(-3deg);
            font-style: italic;
            text-align: center;
            line-height: 1;
            z-index: 2;
            opacity: 1;
            transition: all 0.6s $easeInOutBack 0.2s;

            @include mobile {
                font-size: clamp(45px, 13vw, 64px);
            }

            @include desktop {
                font-size: 70px;
            }

            :deep(small) {
                display: block;
                font-size: 80%;
                color: $yellow;
            }

            .wheel--step-2 &,
            .wheel--step-3 & {
                opacity: 0;
                transform: translate(0, 20px) scale(0.8);
                transition-delay: 0;
            }
        }

        .wheel--spin-mode & {
            @media (max-height: 667px) {
                flex: 0.5; // Reduce to 12.5% in spin mode
            }
        }
    }

    &__container {
        position: absolute;
        bottom: 60px;
        left: 50%;
        width: 150vw;
        height: 150vw;
        min-width: 450px;
        min-height: 450px;
        max-width: 968px;
        max-height: 968px;
        transform: translate3d(-50%, 0, 0);
        z-index: 1;
        transition: transform var(--windup-time) $spinModeEase;
        transform-origin: center bottom;
        will-change: transform;

        @include mobile {
            bottom: 50px;
        }

        @include tablet {
            bottom: -60px;
        }

        @media (max-width: 599px) {
            .wheel--spin-mode & {
                transform: translate3d(-50%, 30px, 0) scale(1.2);
            }

            .wheel--step-3 & {
                transform: translate3d(-50%, 30px, 0) scale(1.45);
                transition: transform var(--windup-time) $zoomEase;
            }

            .wheel--step-4 & {
                transition: transform 1s $rewardEase;
            }
        }

        @media (min-height: 760px) and (max-width: 599px) {
            .wheel--spin-mode & {
                transform: translate3d(-50%, 50px, 0) scale(1.45);
            }

            .wheel--step-3 & {
                transform: translate3d(-50%, 50px, 0) scale(1.65);
            }
        }
    }

    &__bg {
        top: 20%;

        svg {
            position: absolute;
            top: 0;
            left: 50%;
            width: 181px;
            height: 373px;
            opacity: 0.1;
            transform-origin: 50% 100%;
            transform: translate(-50%, -50%);

            @media (min-height: 740px) and (max-width: 599px) {
                top: -10%;
                width: 272px;
                height: 560px;
            }

            @include tablet {
                width: 362px;
                height: 746px;
            }

            @include desktop-xl {
                width: 543px;
                height: 1119px;
            }

            path {
                fill: #fff;
            }

            &:nth-child(2) {
                transform: translate(-50%, -50%) rotate(45deg);
            }
            &:nth-child(3) {
                transform: translate(-50%, -50%) rotate(90deg);
            }
            &:nth-child(4) {
                transform: translate(-50%, -50%) rotate(135deg);
            }
            &:nth-child(5) {
                transform: translate(-50%, -50%) rotate(180deg);
            }
            &:nth-child(6) {
                transform: translate(-50%, -50%) rotate(225deg);
            }
            &:nth-child(7) {
                transform: translate(-50%, -50%) rotate(270deg);
            }
            &:nth-child(8) {
                transform: translate(-50%, -50%) rotate(315deg);
            }
        }
    }

    &__button {
        position: absolute;
        bottom: #{$terms-offset}px;
        left: 50%;
        transform: translateX(-50%);
        width: 83vw;
        max-width: 300px;
        height: 76px;
        padding: 5px;
        font-size: 24px;
        font-weight: 800;
        text-transform: uppercase;
        z-index: 3;
        -webkit-tap-highlight-color: transparent; // <- Add to core
        transition: transform var(--windup-time) $spinModeEase;

        @media (max-height: 600px) and (orientation: landscape) {
            bottom: 25%;
        }

        @include desktop {
            height: 86px;
            max-width: 360px;
            padding: 6px;
        }

        &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            width: 80%;
            height: 20px;
            border-radius: 46%;
            opacity: 0.34;
            background: #fff;
            mix-blend-mode: color-dodge;
            filter: blur(8px);
            transform: translate(-50%, -100%);
            z-index: 2;
            transition: all 0.25s $easeOutQuart;
        }

        &:deep(.btn__content) {
            transition: all 0.25s $easeOutQuart;
            z-index: 3;
        }

        &:not(.btn--disabled):active {
            box-shadow: inset 0 0 6px 6px rgba(0, 0, 0, 0.1);

            &::before {
                opacity: 0.1;
                transform: translate(-50%, -80%);
            }

            :deep(.btn__container) {
                box-shadow: inset 0 0 4px 0px rgba(0, 0, 0, 0.15);
            }

            &:deep(.btn__content) {
                opacity: 0.95;
                transform: scale(0.96);
            }
        }

        .wheel--spin-mode &:not(.btn--disabled) {
            animation: buttonWiggle 3500ms ease-in-out infinite;
            will-change: transform;
        }
    }

    &__image {
        transform: translate3d(0, 50%, 0) rotate(0deg);
        contain: layout style paint;

        &--wheel {
            // Rules set to keep wheel image sharp when scaled
            will-change: transform;
            backface-visibility: hidden;
            transform-style: preserve-3d;
        }

        &--mask {
            opacity: 0;
            transform: translate3d(0, 50%, 0);
            animation: appear 2.4s $easeOutQuart forwards 1s;
        }
    }

    &__marker {
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translate3d(-50%, 8px, 0) scale(1.08);
        width: 25%;
        height: auto;
        z-index: 3;
        pointer-events: none;
    }
}

.footer {
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 2rem 2rem 2rem;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
    }
}

@keyframes buttonWiggle {
    0%,
    33.33% {
        transform: translateX(-50%) rotate(0);
    }
    35% {
        transform: translateX(-50%) rotate(-3deg);
    }
    37% {
        transform: translateX(-50%) rotate(3deg);
    }
    39% {
        transform: translateX(-50%) rotate(-2deg);
    }
    41% {
        transform: translateX(-50%) rotate(2deg);
    }
    43% {
        transform: translateX(-50%) rotate(-1deg);
    }
    45% {
        transform: translateX(-50%) rotate(1deg);
    }
    47%,
    100% {
        transform: translateX(-50%) rotate(0);
    }
}
</style>
